import { Text } from "@/components/ui";
import { cn } from "@/lib/cn";

type PromotionalRibbonProps = {
  isPromotional?: boolean;
  text?: string;
  position?: "left" | "right";
  size?: "sm" | "md";
  className?: string;
};

const FOLD_COLOR = "color-mix(in srgb, rgb(var(--color-danger)) 70%, rgb(0 0 0))";

const SIZES = {
  sm: { box: 80, strip: 120, top: 18, offset: -28, py: 3, fs: "10px", fold: 4 },
  md: { box: 110, strip: 160, top: 20, offset: -37, py: 5, fs: "13px", fold: 5 },
} as const;

export default function PromotionalRibbon({
  isPromotional = false,
  text,
  position = "right",
  size = "md",
  className,
}: PromotionalRibbonProps) {
  if (!isPromotional) return null;

  const label = (text?.trim() || "PROMO").slice(0, 12);
  const isRight = position === "right";
  const d = SIZES[size];

  return (
    <div
      className={cn("pointer-events-none absolute z-raised-2", className)}
      style={{
        width: d.box,
        height: d.box,
        overflow: "hidden",
        top: -d.fold,
        ...(isRight ? { right: -d.fold } : { left: -d.fold }),
      }}
    >
      <span
        className="absolute h-0 w-0 border-solid"
        style={{
          ...(isRight
            ? { top: 0, left: d.fold, borderWidth: `0 ${d.fold}px ${d.fold}px 0`, borderColor: `transparent ${FOLD_COLOR} transparent transparent` }
            : { top: 0, right: d.fold, borderWidth: `${d.fold}px ${d.fold}px 0 0`, borderColor: `${FOLD_COLOR} transparent transparent transparent` }),
        }}
      />
      <span
        className="absolute h-0 w-0 border-solid"
        style={{
          ...(isRight
            ? { bottom: d.fold, right: 0, borderWidth: `${d.fold}px 0 0 ${d.fold}px`, borderColor: `${FOLD_COLOR} transparent transparent transparent` }
            : { bottom: d.fold, left: 0, borderWidth: `0 0 ${d.fold}px ${d.fold}px`, borderColor: `transparent transparent ${FOLD_COLOR} transparent` }),
        }}
      />
      <span
        className="absolute bg-danger text-danger-foreground shadow-raised"
        style={{
          width: d.strip,
          padding: `${d.py}px 0`,
          top: d.top + d.fold,
          ...(isRight
            ? { right: d.offset, transform: "rotate(45deg)" }
            : { left: d.offset, transform: "rotate(-45deg)" }),
        }}
      >
        <Text
          as="span"
          weight="black"
          align="center"
          italic
          uppercase
          tone="inherit"
          leading="loose"
          tracking="label"
          className="block"
          style={{
            fontSize: d.fs,
            textIndent: "0.08em",
            fontStretch: "condensed",
          }}
        >
          {label}
        </Text>
      </span>
    </div>
  );
}
